import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import debounce from 'lodash.debounce';
import CountryCardMain from './CountryCardMain';
import { useLocation, useNavigate } from 'react-router-dom';
import { useChannelData } from '../context/ChannelDataContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const getColumnCount = (width) => {
  // Denser grid on mobile/tablet to show more countries at once.
  if (width < 420) return 2;
  if (width < 640) return 3;
  if (width < 900) return 4;
  if (width < 1280) return 5;
  return 5;
};

/** Same ordering as restcountries regions where possible */
const REGION_ORDER = ['Africa', 'Europe', 'Americas', 'North America', 'South America', 'Oceania', 'Asia', 'Antarctic', 'Other'];
const SHOW_LOCAL_SOURCE_BANNER = process.env.NODE_ENV !== 'production';

function inferRegionFromTimeZone(timeZone) {
  if (!timeZone || typeof timeZone !== 'string') return 'Other';
  if (timeZone.startsWith('Europe/') || timeZone.startsWith('Arctic/')) return 'Europe';
  if (timeZone.startsWith('Asia/') || timeZone.startsWith('Indian/')) return 'Asia';
  if (timeZone.startsWith('Africa/')) return 'Africa';
  if (timeZone.startsWith('America/')) return 'Americas';
  if (timeZone.startsWith('Pacific/') || timeZone.startsWith('Australia/')) return 'Oceania';
  if (timeZone.startsWith('Antarctica')) return 'Antarctic';
  if (timeZone.startsWith('Atlantic/')) return 'Americas';
  return 'Other';
}

/** When restcountries.com fails (network, CORS, rate limit), build the same card shape from bundled metadata */
function buildCountriesFromMetadata(meta) {
  return Object.entries(meta)
    .filter(([, row]) => row && row.hasChannels === true)
    .map(([cca2, row]) => {
      const code = String(cca2).toUpperCase();
      const name = row.country || code;
      return {
        cca2: code,
        name: { common: name },
        region: inferRegionFromTimeZone(row.timeZone),
        flags: { svg: `https://flagcdn.com/${code.toLowerCase()}.svg` },
      };
    });
}

function sortCountryList(list) {
  return [...list].sort((a, b) => {
    const regionA = a.region || 'Other';
    const regionB = b.region || 'Other';
    const indexA = REGION_ORDER.indexOf(regionA);
    const indexB = REGION_ORDER.indexOf(regionB);
    const safeIndexA = indexA === -1 ? REGION_ORDER.length : indexA;
    const safeIndexB = indexB === -1 ? REGION_ORDER.length : indexB;
    if (safeIndexA !== safeIndexB) return safeIndexA - safeIndexB;
    return (a.name?.common || '').localeCompare(b.name?.common || '', undefined, { sensitivity: 'base' });
  });
}

function SearchIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function ChannelRow({ channel, onPlay }) {
  const initial = (channel.name || '?').slice(0, 2).toUpperCase();

  return (
    <button
      type="button"
      onClick={onPlay}
      className="group flex w-full items-center gap-2.5 rounded-lg border border-slate-700/70 bg-slate-900/45 px-2.5 py-2.5 text-left transition hover:border-brand-500/35 hover:bg-slate-900/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500/40 sm:gap-3.5 sm:rounded-xl sm:px-4 sm:py-3.5"
    >
      <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-slate-800 ring-1 ring-slate-700/80 sm:h-12 sm:w-12">
        {channel.logo ? (
          <img src={channel.logo} alt="" className="h-full w-full object-contain p-1 transition group-hover:scale-[1.04]" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 text-xs font-bold text-brand-400">
            {initial}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1 py-0.5">
        <p className="text-[13px] font-semibold leading-snug text-white sm:text-sm">{channel.name}</p>
        {channel.description ? (
          <p className="mt-0.5 line-clamp-1 text-xs leading-relaxed text-slate-500">{channel.description}</p>
        ) : null}
      </div>
      <span className="shrink-0 text-slate-600 transition group-hover:text-brand-400" aria-hidden>
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </span>
    </button>
  );
}

const VirtualizedGrid = ({ channels, setSelectedCountry, highlightedCountry, setHighLightedCountry, memoizedAllChannels, setSelectedChannel, searchTerm, setSearchTerm }) => {
  const { countriesMetadata: countriesData, refresh, loading } = useChannelData();
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const t = (frText, enText) => (isFr ? frText : enText);
  const parentRef = useRef();
  const [containerWidth, setContainerWidth] = useState(0);
  const [countries, setCountries] = useState([]);
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('All');
  const [countriesLoadState, setCountriesLoadState] = useState('idle');
  /** restcountries API vs bundled countries_metadata.json (+ flagcdn) */
  const [countryListSource, setCountryListSource] = useState('restcountries');
  const [hideLocalSourceBanner, setHideLocalSourceBanner] = useState(false);
  const [mobilePane, setMobilePane] = useState('countries');

  const nav = useNavigate();
  const location = useLocation();

  const debouncedSetTerm = useMemo(() => debounce((val) => setDebouncedTerm(val), 300), []);

  useEffect(() => {
    return () => debouncedSetTerm.cancel();
  }, [debouncedSetTerm]);

  useEffect(() => {
    if (searchTerm.length > 0) {
      debouncedSetTerm(searchTerm);
    }
  }, [location, searchTerm, debouncedSetTerm]);

  useEffect(() => {
    if (!countriesData || typeof countriesData !== 'object') return;

    let cancelled = false;
    setCountriesLoadState('loading');
    setCountryListSource('restcountries');

    const applySorted = (sorted) => {
      if (cancelled) return;
      setCountries(sorted);
      setSelectedCountry(sorted[0]?.cca2);
      setHighLightedCountry(sorted[0]?.cca2);
      setCountriesLoadState('ready');
    };

    const applyLocalFallback = (reason) => {
      if (cancelled) return;
      const local = sortCountryList(buildCountriesFromMetadata(countriesData));
      if (local.length === 0) {
        console.error('Country list: no entries in metadata with hasChannels', reason);
        setCountriesLoadState('error');
        return;
      }
      console.warn('Country list: using local metadata (restcountries unavailable).', reason);
      setCountryListSource('local');
      applySorted(local);
    };

    fetch('https://restcountries.com/v3.1/all')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (!Array.isArray(data)) throw new Error('Invalid country payload');

        const filtered = data.filter((country) => {
          const cca2 = country.cca2;
          return cca2 && countriesData[cca2]?.hasChannels;
        });

        if (filtered.length === 0) {
          applyLocalFallback('API returned no countries matching the channel index');
          return;
        }

        const sorted = sortCountryList(
          filtered.map((c) => ({
            ...c,
            region: c.region || inferRegionFromTimeZone(countriesData[c.cca2]?.timeZone),
          }))
        );

        setCountryListSource('restcountries');
        applySorted(sorted);
      })
      .catch((err) => {
        console.error('restcountries.com failed:', err);
        if (!cancelled) applyLocalFallback(err?.message || 'network error');
      });

    return () => {
      cancelled = true;
    };
  }, [countriesData, setHighLightedCountry, setSelectedCountry]);

  useEffect(() => {
    if (countryListSource === 'restcountries') setHideLocalSourceBanner(false);
  }, [countryListSource]);

  useEffect(() => {
    if (!parentRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(parentRef.current);
    return () => observer.disconnect();
  }, []);

  const columns = getColumnCount(containerWidth);

  const onSearchChange = (e) => {
    const v = e.target.value;
    setSearchTerm(v);
    setSelectedCountry(null);
    debouncedSetTerm(v);
    setHighLightedCountry(null);
  };

  const clearExplorerFilters = useCallback(() => {
    debouncedSetTerm.cancel();
    setSearchTerm('');
    setDebouncedTerm('');
    setSelectedRegion('All');
  }, [debouncedSetTerm, setSearchTerm]);

  const regions = useMemo(() => {
    const allRegions = countries.map((c) => c.region).filter(Boolean);
    return ['All', ...new Set(allRegions)];
  }, [countries]);

  const filteredCountries = useMemo(() => {
    return countries.filter((c) => {
      const matchName = c.name.common.toLowerCase().includes(debouncedTerm.toLowerCase());
      const matchRegion = selectedRegion === 'All' || c.region === selectedRegion;
      return matchName && matchRegion;
    });
  }, [countries, debouncedTerm, selectedRegion]);

  const matchingChannels = useMemo(() => {
    if (!debouncedTerm) return [];
    return memoizedAllChannels.filter((channel) => channel.name.toLowerCase().includes(debouncedTerm.toLowerCase()));
  }, [debouncedTerm, memoizedAllChannels]);

  const selectedCountryName = useMemo(
    () => countries.find((c) => c.cca2 === highlightedCountry)?.name?.common,
    [countries, highlightedCountry]
  );

  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(filteredCountries.length / columns) || 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 116,
    overscan: 6,
  });

  const sidebarMode = !highlightedCountry && matchingChannels.length > 0 ? 'search' : 'country';
  const hasAnyChannels = channels.length > 0 || matchingChannels.length > 0;

  return (
    <div className="flex h-[100dvh] flex-col bg-slate-950 bg-mesh-gradient text-slate-100">
      <header className="shrink-0 border-b border-slate-800/80 bg-slate-950/92 px-3 py-2.5 backdrop-blur-xl sm:px-6 sm:py-4 lg:px-8">
        <div className="mx-auto flex max-w-[1800px] flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
          <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-3">
            <div className="relative min-w-0 flex-1 lg:max-w-xl">
              <label htmlFor="vg-search" className="sr-only">
                {t('Rechercher des pays ou des chaînes', 'Search countries or channels')}
              </label>
              <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                id="vg-search"
                type="search"
                placeholder={t('Rechercher des pays ou des chaînes…', 'Search countries or channels…')}
                value={searchTerm}
                onChange={onSearchChange}
                className="ui-input h-10 rounded-lg pl-10 pr-3 text-sm sm:h-12 sm:rounded-xl"
              />
            </div>
            <div className="w-full shrink-0 sm:w-[min(100%,220px)] lg:w-52">
              <label htmlFor="vg-region" className="sr-only">
                {t('Filtre par région', 'Region filter')}
              </label>
              <select
                id="vg-region"
                className="ui-select h-10 rounded-lg text-sm sm:h-12 sm:rounded-xl"
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
              >
                {regions.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
            <button type="button" onClick={() => nav('/')} className="ui-btn-secondary h-10 px-3.5 text-xs sm:h-11 sm:px-4 sm:text-sm">
              {t('Accueil', 'Home')}
            </button>
            <button
              type="button"
              onClick={() => refresh()}
              disabled={loading}
              className="ui-btn-primary h-10 px-3.5 text-xs sm:h-11 sm:px-4 sm:text-sm"
              title={t('Recharger les données des chaînes', 'Reload channel data')}
            >
              {loading ? (
                <>
                  <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  {t('Mise à jour', 'Updating')}
                </>
              ) : (
                <>{t('Actualiser les données', 'Refresh data')}</>
              )}
            </button>
            <LanguageSwitcher compact />
          </div>
        </div>
        <div className="mt-2.5 lg:hidden">
          <div className="grid grid-cols-2 gap-1.5 rounded-lg border border-slate-800/80 bg-slate-900/50 p-1">
            <button
              type="button"
              onClick={() => setMobilePane('countries')}
              className={`rounded-md px-2.5 py-2 text-xs font-semibold transition ${
                mobilePane === 'countries'
                  ? 'bg-brand-600/90 text-white shadow-sm'
                  : 'text-slate-300 hover:bg-slate-800/80'
              }`}
            >
              {t('Pays', 'Countries')}
            </button>
            <button
              type="button"
              onClick={() => setMobilePane('channels')}
              className={`rounded-md px-2.5 py-2 text-xs font-semibold transition ${
                mobilePane === 'channels'
                  ? 'bg-brand-600/90 text-white shadow-sm'
                  : 'text-slate-300 hover:bg-slate-800/80'
              }`}
            >
              {t('Chaînes', 'Channels')}
              {hasAnyChannels ? (
                <span className="ml-1.5 text-[11px] opacity-90">
                  ({sidebarMode === 'country' ? channels.length : matchingChannels.length})
                </span>
              ) : null}
            </button>
          </div>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[1fr_minmax(300px,400px)] xl:grid-cols-[1fr_minmax(320px,420px)]">
        <div
          className={`relative min-h-0 overflow-auto border-b border-slate-800/80 lg:border-b-0 lg:border-r lg:border-slate-800/80 ${
            mobilePane === 'channels' ? 'hidden lg:block' : 'block'
          }`}
          ref={parentRef}
        >
          {countriesLoadState === 'loading' || (countriesLoadState === 'idle' && countries.length === 0) ? (
            <div className="flex min-h-[45vh] flex-col items-center justify-center gap-4 px-6 py-16 text-center">
              <div className="h-11 w-11 animate-spin rounded-full border-2 border-slate-700 border-t-brand-500" />
              <div>
                <p className="text-base font-semibold text-slate-200">{t('Chargement des pays', 'Loading countries')}</p>
                <p className="mt-2 max-w-sm text-sm text-slate-500">{t("Construction de la grille des régions disposant de chaînes dans l'index.", 'Building the grid of regions that have channels in the index.')}</p>
              </div>
            </div>
          ) : countriesLoadState === 'error' ? (
            <div className="flex min-h-[45vh] flex-col items-center justify-center gap-4 px-6 py-16 text-center">
              <p className="text-base font-semibold text-rose-200">{t('Impossible de construire la liste des pays', 'Could not build country list')}</p>
              <p className="max-w-md text-sm text-slate-400">
                {t("Le répertoire des pays en ligne et l'index local des chaînes n'ont pas pu produire de liste. Vérifiez votre connexion puis rechargez.", 'The online country directory and the local channel index both failed to produce a list. Check your connection, then reload.')}
              </p>
              <button type="button" className="ui-btn-primary mt-2" onClick={() => window.location.reload()}>
                {t('Recharger la page', 'Reload page')}
              </button>
            </div>
          ) : filteredCountries.length === 0 ? (
            <div className="flex min-h-[45vh] flex-col items-center justify-center px-6 py-16">
              <div className="mx-auto max-w-md rounded-2xl border border-slate-800/90 bg-slate-900/50 p-8 text-center shadow-panel ring-1 ring-white/[0.03]">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-700 bg-slate-800/60 text-slate-400">
                  <SearchIcon className="h-6 w-6" />
                </div>
                <h2 className="mt-6 text-lg font-semibold text-white">{t('Aucun pays ne correspond', 'No countries match')}</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">
                  {t('Essayez un autre terme de recherche ou mettez le filtre région sur “All” pour voir la grille complète.', 'Try another search term or set the region filter to “All” to see the full grid.')}
                </p>
                <button type="button" className="ui-btn-primary mt-8 w-full sm:w-auto" onClick={clearExplorerFilters}>
                  {t('Réinitialiser les filtres', 'Reset filters')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="sticky top-0 z-[1] border-b border-slate-800/60 bg-slate-950/95 px-3 py-2.5 backdrop-blur-md sm:px-5 sm:py-3">
                {SHOW_LOCAL_SOURCE_BANNER && countryListSource === 'local' && !hideLocalSourceBanner ? (
                  <div className="mb-3 flex flex-col gap-2 rounded-lg border border-amber-900/40 bg-amber-950/30 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-[11px] leading-relaxed text-amber-100/90">
                      {t("La liste des pays est chargée depuis l'index de l'application (l'annuaire en ligne n'a pas répondu). Les groupes de régions sont approximatifs.", 'Country list is loaded from your app index (the online directory did not respond). Region groups are approximate.')}
                    </p>
                    <button
                      type="button"
                      onClick={() => setHideLocalSourceBanner(true)}
                      className="shrink-0 self-end rounded-md px-2 py-1 text-[11px] font-medium text-amber-200/90 transition hover:bg-amber-900/50 sm:self-auto"
                    >
                      Masquer
                    </button>
                  </div>
                ) : null}
                <p className="text-xs font-medium text-slate-500">
                  <span className="font-semibold text-slate-300">{filteredCountries.length}</span> {t('pays', 'countries')}
                  {selectedRegion !== 'All' ? (
                    <>
                      {' '}
                      · <span className="text-slate-400">{selectedRegion}</span>
                    </>
                  ) : null}
                </p>
                <p className="mt-1 text-[11px] text-slate-600">{t('Sélectionnez une carte pour charger ses chaînes dans la colonne de droite.', 'Select a card to load channels in the right column.')}</p>
              </div>
              <div className="relative mx-auto max-w-[1800px] p-2 sm:p-4" style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const startIdx = virtualRow.index * columns;
                  const items = filteredCountries.slice(startIdx, startIdx + columns);
                  return (
                    <div
                      key={virtualRow.key}
                      ref={virtualRow.measureElement}
                      className="absolute left-0 grid w-full gap-2 px-1 pb-2 sm:gap-4 sm:px-2 sm:pb-3"
                      style={{
                        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      {items.map((country) => (
                        <button
                          key={country.cca2}
                          type="button"
                          className="text-left"
                          onClick={() => {
                            setSelectedCountry(country.cca2);
                            setHighLightedCountry(country.cca2);
                        setMobilePane('channels');
                          }}
                        >
                          <CountryCardMain country={country} isHighlighted={highlightedCountry === country.cca2} />
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <aside className={`min-h-0 flex-col border-slate-800 bg-slate-950/70 lg:flex lg:border-l lg:border-slate-800/80 ${
          mobilePane === 'countries' ? 'hidden' : 'flex'
        }`}>
          <div className="shrink-0 border-b border-slate-800/80 px-3 py-3 sm:px-5 sm:py-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-bold tracking-tight text-white sm:text-base">{t('Chaînes', 'Channels')}</h2>
              {(channels.length > 0 && sidebarMode === 'country') || (matchingChannels.length > 0 && sidebarMode === 'search') ? (
                <span className="inline-flex items-center rounded-full border border-slate-700/90 bg-slate-900/80 px-2.5 py-1 text-[11px] font-semibold tabular-nums text-slate-300 ring-1 ring-white/5">
                  {sidebarMode === 'country' ? channels.length : matchingChannels.length}{' '}
                  {sidebarMode === 'country' ? t('chaînes', 'channels') : t('résultats', 'matches')}
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-slate-500 sm:text-sm">
              {sidebarMode === 'search'
                ? t('Résultats de votre recherche — choisissez-en une pour lancer la lecture.', 'Matches from your search. Pick one to play.')
                : selectedCountryName
                  ? (
                    <>
                      <span className="text-slate-400">{t('Sélectionné :', 'Selected:')}</span>{' '}
                      <span className="font-medium text-slate-300">{selectedCountryName}</span>
                      {highlightedCountry ? (
                        <span className="ml-1.5 font-mono text-xs text-slate-600">({String(highlightedCountry).toUpperCase()})</span>
                      ) : null}
                    </>
                  )
                  : t('Sélectionnez un pays à gauche pour charger sa liste.', 'Select a country on the left to load its list.')}
            </p>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-2.5 py-3 sm:px-4 sm:py-5">
            {!highlightedCountry && matchingChannels.length > 0 ? (
              <ul className="flex flex-col gap-3">
                {matchingChannels.map((channel, index) => (
                  <li key={`${channel.name}-${index}`}>
                    <ChannelRow
                      channel={channel}
                      onPlay={() => {
                        setSelectedChannel(channel);
                        nav(`/player?search=${encodeURIComponent(searchTerm)}`);
                      }}
                    />
                  </li>
                ))}
              </ul>
            ) : channels.length > 0 ? (
              <ul className="flex flex-col gap-3">
                {channels.map((channel, index) => (
                  <li key={`${channel.name}-${index}`}>
                    <ChannelRow
                      channel={channel}
                      onPlay={() => {
                        setSelectedChannel(channel);
                        nav(`/player?search=${encodeURIComponent(searchTerm)}&highlightedCountry=${highlightedCountry}`);
                      }}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-700/80 bg-slate-900/30 px-6 py-12 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-slate-700 bg-slate-800/50 text-brand-400/90">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="mt-5 text-sm font-semibold text-slate-300">{t('Aucune liste à afficher pour le moment', 'No list to show yet')}</p>
                <p className="mt-2 max-w-[240px] text-xs leading-relaxed text-slate-500">
                  {t("Choisissez un pays dans la grille, ou lancez une recherche globale : les chaînes correspondantes s'affichent ici.", 'Choose a country from the grid, or search globally. Matching channels appear here.')}
                </p>
                {debouncedTerm || selectedRegion !== 'All' ? (
                  <button type="button" className="ui-btn-secondary mt-6 text-xs" onClick={clearExplorerFilters}>
                    {t('Réinitialiser les filtres de gauche', 'Reset left filters')}
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default VirtualizedGrid;
